import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import FormattedCurrency from 'components/ui/FormattedCurrency';
import SectionHeaderBar from 'components/ui/SectionHeaderBar';
import { TableHeader, TableRow } from 'components/ui/StyledTable';
import { legsShape } from 'constants/propTypes';
import VatAddress from './VatAddress';
import CountriesVAT from './CountriesVAT';

import { VatBreakdownContainer, StyledTable, StyledCell, StyledPassengerName } from './VatBreakdown.styles';

const PARSE_DATE_FORMAT = 'YYYY-MM-DD';
const DATE_FORMAT = 'ddd Do MMMM YYYY';

const VatBreakdownTable = ({ direction, ticketNumber, origin, destination, departureDate, vat, serviceTypeCode }) => (
  <React.Fragment>
    <h4>
      <FormattedMessage id={`journeySummary.${direction}`} /> -{' '}
      {`${moment(departureDate, PARSE_DATE_FORMAT).format(DATE_FORMAT)} - ${origin}`} <FormattedMessage id="bookingMagnet.to" />{' '}
      {destination}
    </h4>
    <StyledTable data-cy="vat-breakdown-table">
      <TableHeader columns={12} data-cy="vat-breakdown-table-header">
        <StyledCell span={4}>
          <FormattedMessage id="journeySegment.ticketNumber" /> {ticketNumber}
        </StyledCell>
        <StyledCell span={2}>
          <FormattedMessage id="receipt.excludingVat" />
        </StyledCell>
        <StyledCell span={2}>
          <FormattedMessage id="receipt.vatRate" />
        </StyledCell>
        <StyledCell span={2}>
          <FormattedMessage id="receipt.vat" />
        </StyledCell>
        <StyledCell span={2}>
          <FormattedMessage id="receipt.includingVat" />
        </StyledCell>
      </TableHeader>
      {vat.map(({ countryCode, rate, amount, price: { currency, excludingVat, includingVat } = {} }) => (
        <TableRow data-cy="vat-breakdown-table-row" columns={12} key={countryCode}>
          <StyledCell span={4}>
            <CountriesVAT countryCode={countryCode} serviceTypeCode={serviceTypeCode} />
          </StyledCell>
          <StyledCell span={2}>{excludingVat ? <FormattedCurrency amount={excludingVat} currency={currency} /> : null}</StyledCell>
          <StyledCell span={2}>{(rate * 100).toFixed(2)}%</StyledCell>
          <StyledCell span={2}>
            <FormattedCurrency amount={amount} currency={currency} />
          </StyledCell>
          <StyledCell span={2}>{includingVat ? <FormattedCurrency amount={includingVat} currency={currency} /> : null}</StyledCell>
        </TableRow>
      ))}
    </StyledTable>
  </React.Fragment>
);

const VatBreakdown = ({ outbound = [], inbound = [] }) => {
  const vatInfo = useMemo(() => {
    const vatObject = {
      passengers: {},
      countries: []
    };

    [...outbound, ...inbound].forEach(({ origin, destination, timing, direction, products, serviceType }) => {
      products.forEach(({ canShowVAT, vat = [], passenger: { id, firstName, lastName } = {}, ticketNumber }) => {
        if (canShowVAT) {
          const filteredVats = vat.filter(({ amount, countryCode }) => {
            if (amount > 0) {
              vatObject.countries.push({ countryCode, serviceTypeCode: serviceType?.code });
              return true;
            }

            return false;
          });

          if (filteredVats.length > 0) {
            const vatData = {
              ticketNumber,
              origin: origin.name,
              destination: destination.name,
              departureDate: timing.date,
              serviceType,
              vat: filteredVats
            };

            if (!vatObject?.passengers?.[id]) {
              vatObject.passengers[id] = {
                passengerFirstName: firstName,
                passengerLastName: lastName,
                [direction]: [vatData]
              };
            } else if (!vatObject?.passengers?.[id]?.[direction]) {
              vatObject.passengers[id][direction] = [vatData];
            } else {
              vatObject.passengers[id][direction].push(vatData);
            }
          }
        }
      });
    });

    return vatObject;
  }, [outbound, inbound]);

  if (Object.keys(vatInfo.passengers).length === 0) {
    return null;
  }

  return (
    <>
      <SectionHeaderBar>
        <FormattedMessage id="receipt.paymentsVat" />
      </SectionHeaderBar>
      <VatBreakdownContainer data-cy="vat-breakdown">
        {Object.entries(vatInfo.passengers).map(([id, { passengerFirstName, passengerLastName, outbound, inbound }]) => (
          <React.Fragment key={id}>
            <StyledPassengerName>
              {passengerFirstName} {passengerLastName}
            </StyledPassengerName>
            {outbound && outbound.length > 0
              ? outbound.map(({ ticketNumber, origin, destination, departureDate, vat, serviceType }) => (
                  <VatBreakdownTable
                    key={ticketNumber}
                    direction="outbound"
                    ticketNumber={ticketNumber}
                    origin={origin}
                    destination={destination}
                    departureDate={departureDate}
                    serviceTypeCode={serviceType?.code}
                    vat={vat}
                  />
                ))
              : null}
            {inbound && inbound.length > 0
              ? inbound.map(({ ticketNumber, origin, destination, departureDate, vat, serviceType }) => (
                  <VatBreakdownTable
                    key={ticketNumber}
                    direction="inbound"
                    ticketNumber={ticketNumber}
                    origin={origin}
                    destination={destination}
                    serviceTypeCode={serviceType?.code}
                    departureDate={departureDate}
                    vat={vat}
                  />
                ))
              : null}
          </React.Fragment>
        ))}
      </VatBreakdownContainer>
      <VatAddress vatInfo={vatInfo.countries} dataCy="vat-addresses" />
    </>
  );
};

VatBreakdown.propTypes = {
  outbound: legsShape,
  inbound: legsShape
};

VatBreakdownTable.propTypes = {
  direction: PropTypes.string,
  ticketNumber: PropTypes.string,
  origin: PropTypes.string,
  destination: PropTypes.string,
  departureDate: PropTypes.string,
  serviceTypeCode: PropTypes.string,
  vat: PropTypes.arrayOf(
    PropTypes.shape({
      countryCode: PropTypes.string,
      rate: PropTypes.number,
      amount: PropTypes.number,
      price: PropTypes.shape({
        currency: PropTypes.string,
        excludingVat: PropTypes.number,
        includingVat: PropTypes.number
      })
    })
  )
};

export default VatBreakdown;
